import { useEffect, useState, useCallback, useRef } from "react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";

export function useOrgSelection() {
  const { data: organizations, isPending: isLoadingOrgs } = authClient.useListOrganizations();
  const { data: activeOrg, isPending: isLoadingActiveOrg } = authClient.useActiveOrganization();

  // États pour la gestion de l'organisation
  const [showOrgSelection, setShowOrgSelection] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isSettingActive, setIsSettingActive] = useState(false);

  // Refs pour éviter les actions multiples
  const hasProcessedOrgsRef = useRef(false);
  const lastProcessedOrgIdRef = useRef<string | null>(null);
  const isProcessingOrgRef = useRef(false);

  // Fonction pour définir l'organisation active
  const handleSetActiveOrganization = useCallback(async (organizationId: string) => {
    // Vérifier si on traite déjà cette organisation
    if (isProcessingOrgRef.current || lastProcessedOrgIdRef.current === organizationId) {
      return;
    }

    isProcessingOrgRef.current = true;
    lastProcessedOrgIdRef.current = organizationId;
    setIsSettingActive(true);

    try {
      await authClient.organization.setActive({
        organizationId,
      });

      // Enlever car un peu inutile
      //toast.success("Organisation sélectionnée avec succès", { id: "org-selection-success" });
      setShowOrgSelection(false);
      setIsSettingActive(false);
      hasProcessedOrgsRef.current = true;
    } catch (error) {
      // En cas d'erreur, permettre un nouvel essai
      lastProcessedOrgIdRef.current = null;
      setIsSettingActive(false);
      toast.error("Erreur lors de la sélection de l'organisation", {
        description: (error as Error).message,
      });
    } finally {
      isProcessingOrgRef.current = false;
    }
  }, []);

  // Effect pour vérifier et gérer les organisations
  useEffect(() => {
    if (isLoadingOrgs || isLoadingActiveOrg) return;
    if (hasProcessedOrgsRef.current || isProcessingOrgRef.current) return;

    // Si pas d'organisations du tout, ne rien faire
    if (!organizations || organizations.length === 0) {
      return;
    }

    // Si l'utilisateur a déjà une organisation active
    if (activeOrg) {
      hasProcessedOrgsRef.current = true;
      // Reset le tracking si l'organisation active change
      if (lastProcessedOrgIdRef.current !== activeOrg.id) {
        lastProcessedOrgIdRef.current = activeOrg.id;
      }
      return;
    }

    // Si exactement 1 organisation, la sélectionner automatiquement
    if (organizations.length === 1) {
      const orgId = organizations[0].id;
      // Vérifier qu'on n'a pas déjà traité cette organisation
      if (lastProcessedOrgIdRef.current !== orgId) {
        console.log("Only one organization available, setting it as active...");
        handleSetActiveOrganization(orgId);
      }
      return;
    }

    // Si plusieurs organisations, afficher le dialog de sélection
    if (organizations.length > 1) {
      console.log("Multiple organizations found, showing selection dialog...");
      setSelectedOrgId(organizations[0].id);
      setShowOrgSelection(true);
      hasProcessedOrgsRef.current = true;
    }
  }, [organizations, activeOrg, isLoadingOrgs, isLoadingActiveOrg, handleSetActiveOrganization]);

  // Reset des refs quand les données critiques changent
  useEffect(() => {
    if (isLoadingOrgs || isLoadingActiveOrg) {
      // Ne reset le processing que si on n'a pas d'organisation active
      if (!activeOrg) {
        hasProcessedOrgsRef.current = false;
        // On évite de reset lastProcessedOrgIdRef ici pour éviter les boucles
      }
    }
  }, [isLoadingOrgs, isLoadingActiveOrg, activeOrg]);

  // Fonction pour forcer la vérification des organisations (utile pour déclencher manuellement)
  const checkOrgSelection = useCallback(() => {
    hasProcessedOrgsRef.current = false;
    lastProcessedOrgIdRef.current = null;
  }, []);

  // Déterminer si on a besoin d'une organisation active mais qu'elle n'est pas encore définie
  // Cela se produit quand on a des organisations mais pas encore d'activeOrg
  const needsActiveOrg = !!(organizations && organizations.length > 0 && !activeOrg);

  // Si on n'a pas d'organisations, on ne devrait pas attendre le chargement de l'organisation active
  // car elle sera forcément nulle ou invalide. Cela évite de bloquer l'UI si useActiveOrganization
  // échoue ou boucle (ex: 400 Bad Request quand pas d'org).
  // On inclut aussi isSettingActive pour que le loading persiste pendant qu'on définit l'org
  const isPending =
    isLoadingOrgs || (organizations && organizations.length > 0 ? isLoadingActiveOrg : false) || isSettingActive || (needsActiveOrg && !showOrgSelection); // Attendre tant qu'on a besoin d'une org active mais qu'on n'a pas encore affiché le dialogue

  return {
    organizations,
    activeOrg,
    isPending,
    // Indique si on attend une sélection d'organisation (utilisateur doit choisir parmi plusieurs)
    isWaitingForOrgSelection: showOrgSelection,
    // États et fonctions pour la sélection d'organisation
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection: () => setShowOrgSelection(false),
    checkOrgSelection,
  };
}
